import type { PatchEndpoint, PatchGraphV1 } from '../../packages/routing-graph/src/index.js';
import type { LivePlan } from './live-graph-core.js';
export interface LiveSnapshot {graph:PatchGraphV1;outputs:readonly PatchEndpoint[];}
export const LIVE_HOST_LIMITS=Object.freeze({renderAckTimeoutMs:20000,editIntervalMs:50});
export interface LiveGraphDependencies {
 readonly ackTimeoutMs?:number;
 load():Promise<void>;resume():Promise<void>;close():void;
 createNode(plan:LivePlan,epoch:number,revision:number):AudioWorkletNode;
 prepare(snapshot:LiveSnapshot):{promise:Promise<LivePlan>;cancel():void};
}
/** Main-thread lifecycle only. Audio cadence belongs to the Worklet. At most one
 * compiler request is in flight; update storms overwrite a single snapshot.
 */
export class LiveGraphController {
 private epoch=0;private requested=0;private applied=-1;private wanted=false;private ready=false;
 private node:AudioWorkletNode|undefined;private snapshot:LiveSnapshot|undefined;
 private cancel:(()=>void)|undefined;private startup:ReturnType<typeof setTimeout>|undefined;
 private timer:ReturnType<typeof setTimeout>|undefined;private inFlight=false;
 private cancelReady:(()=>void)|undefined;
 private awaiting:number|undefined;private applyTimer:ReturnType<typeof setTimeout>|undefined;
 constructor(private readonly deps:LiveGraphDependencies,private readonly report:(state:string,data?:Record<string,unknown>)=>void){}
 get active(){return this.wanted;}
 get appliedRevision(){return this.applied;}
 get requestedRevision(){return this.requested;}
 private current(epoch:number){return this.wanted&&epoch===this.epoch;}
 async start(graph:PatchGraphV1,outputs:readonly PatchEndpoint[]){
  this.stop(false);this.wanted=true;this.snapshot={graph:structuredClone(graph),outputs:structuredClone(outputs)};
  const epoch=this.epoch;this.requested++;this.report('starting');
  this.startup=setTimeout(()=>{if(this.current(epoch))this.fail(new Error('Live startup timed out'));},20000);
  try{
   while(this.current(epoch)){
    const revision=this.requested,task=this.deps.prepare(this.snapshot!);this.cancel=task.cancel;
    let plan:LivePlan;
    try{plan=await task.promise;}catch(error){if(!this.current(epoch))return;if(revision!==this.requested)continue;throw error;}
    if(!this.current(epoch))return;this.cancel=undefined;if(revision!==this.requested)continue;
    await this.deps.load();if(!this.current(epoch))return;if(revision!==this.requested)continue;
    this.awaiting=revision;this.node=this.deps.createNode(plan,epoch,revision);
    const node=this.node;
    const gotReady=await new Promise<boolean>(resolve=>{
     this.cancelReady=()=>resolve(false);
     node.onprocessorerror=()=>{if(this.current(epoch))this.fail(new Error('AudioWorklet processor failed'));};
     node.port.onmessage=(event:MessageEvent)=>{
      const m=event.data;if(!this.current(epoch)||m.epoch!==epoch)return;
      if(m.type==='error'){this.fail(new Error(m.error));return;}
      if(m.type==='ready'){this.report('prewarmed',m);this.cancelReady=undefined;resolve(true);return;}
      if(m.type==='installed'||m.type==='meter'){
       this.applied=Math.max(this.applied,m.revision);
       if(m.type==='installed')clearTimeout(this.startup);
       if(this.awaiting!==undefined&&this.applied>=this.awaiting){this.awaiting=undefined;clearTimeout(this.applyTimer);}
       if(this.applied<this.requested)this.schedule();
       this.report(this.applied===this.requested?'playing':'preparing',{...m,requestedRevision:this.requested,appliedRevision:this.applied});
      }
     };
    });
    if(!gotReady||!this.current(epoch))return;
    await this.deps.resume();if(!this.current(epoch))return;
    this.ready=true;if(this.requested!==revision)this.schedule();return;
   }
  }catch(error){if(this.current(epoch))this.fail(error);}
 }
 update(graph:PatchGraphV1,outputs:readonly PatchEndpoint[]){
  if(!this.wanted)return;this.snapshot={graph:structuredClone(graph),outputs:structuredClone(outputs)};this.requested++;
  this.report('preparing',{requestedRevision:this.requested,appliedRevision:this.applied});if(this.ready)this.schedule();
 }
 private schedule(){if(this.timer||this.inFlight||!this.ready||this.awaiting!==undefined)return;const epoch=this.epoch;this.timer=setTimeout(()=>{this.timer=undefined;if(this.current(epoch))void this.install(epoch);},LIVE_HOST_LIMITS.editIntervalMs);}
 private async install(epoch:number){
  const revision=this.requested;this.inFlight=true;
  try{
   const task=this.deps.prepare(this.snapshot!);this.cancel=task.cancel;const plan=await task.promise;
   if(!this.current(epoch))return;
   if(revision===this.requested){
    this.awaiting=revision;clearTimeout(this.applyTimer);
    this.applyTimer=setTimeout(()=>{if(this.current(epoch)&&this.applied<revision)this.fail(new Error('Rendered graph ACK timed out: '+revision));},this.deps.ackTimeoutMs??LIVE_HOST_LIMITS.renderAckTimeoutMs);
    this.node!.port.postMessage({type:'install',epoch,revision,plan});
   }
  }catch(error){if(this.current(epoch)&&revision===this.requested)this.fail(error);}
  finally{if(this.current(epoch)){this.inFlight=false;this.cancel=undefined;if(revision!==this.requested)this.schedule();}}
 }
 stop(announce=true){
  this.epoch++;this.wanted=false;this.ready=false;this.inFlight=false;this.applied=-1;
  clearTimeout(this.applyTimer);this.awaiting=undefined;
  clearTimeout(this.startup);clearTimeout(this.timer);this.timer=undefined;this.cancel?.();this.cancel=undefined;
  this.cancelReady?.();this.cancelReady=undefined;
  if(this.node){this.node.port.postMessage({type:'stop'});this.node.disconnect();this.node.port.close();this.node=undefined;}
  this.deps.close();if(announce)this.report('stopped');
 }
 private fail(error:unknown){this.stop(false);this.report('error',{error:error instanceof Error?error.message:String(error)});}
}
/** Own dedicated context is suspended until Worklet's actual ready ACK. No DSP
 * constructors are scheduled into a context already playing the fixed host.
 */
export function createBrowserLiveGraph(report:(state:string,data?:Record<string,unknown>)=>void){
 let context:AudioContext|undefined,resumedContext:AudioContext|undefined,loading:Promise<void>|undefined,worker:Worker|undefined,serial=0;
 let pending:{id:number;resolve:(p:LivePlan)=>void;reject:(e:Error)=>void;timer:ReturnType<typeof setTimeout>}|undefined;
 const closeWorker=()=>{if(pending){clearTimeout(pending.timer);pending.reject(new Error('Live compiler closed'));pending=undefined;}worker?.terminate();worker=undefined;};
 const getContext=()=>{
  if(context)return context;const own=context=new AudioContext();
  own.addEventListener('statechange',()=>{
   const state=own.state as string;
   if(context===own&&resumedContext===own&&controller.active&&(state==='suspended'||state==='interrupted')){
    controller.stop(false);report('error',{error:`Live context ${state}; explicit Start required`});
   }
  });return own;
 };
 const controller=new LiveGraphController({
  load:()=>{
   const own=getContext();
   if(!loading)loading=own.suspend().then(()=>own.audioWorklet.addModule(new URL('./live-graph-worklet.js',import.meta.url).href));return loading;
  },
  resume:()=>{const own=context!;resumedContext=own;return own.resume();},
  close:()=>{closeWorker();const own=context;context=undefined;resumedContext=undefined;loading=undefined;if(own&&own.state!=='closed')void own.close().catch(()=>{});},
  createNode:(plan,epoch,revision)=>{
   if(context!.state!=='suspended')throw new Error('Worklet preparation requires suspended live context');
   const node=new AudioWorkletNode(context!,'allmost-live-graph',{numberOfInputs:0,numberOfOutputs:1,outputChannelCount:[2],processorOptions:{plan,epoch,revision}});node.connect(context!.destination);return node;
  },
  prepare:snapshot=>{
   if(pending)throw new Error('Live compiler request already in flight');
   if(!worker){
    worker=new Worker(new URL('./live-graph-worker.js',import.meta.url),{type:'module'});
    worker.onmessage=event=>{const p=pending;if(!p||event.data.id!==p.id)return;pending=undefined;clearTimeout(p.timer);if(event.data.ok)p.resolve(event.data.plan);else p.reject(new Error(event.data.error));};
    worker.onerror=event=>{const p=pending;pending=undefined;if(p){clearTimeout(p.timer);p.reject(new Error(event.message));}};
   }
   // Worker compiler needs the future live context's actual sample rate. Creating
   // this suspended context does not instantiate DSP or resume transport.
   const own=getContext(),id=++serial;
   const promise=new Promise<LivePlan>((resolve,reject)=>{
    const timer=setTimeout(()=>{if(pending?.id===id)closeWorker();},10000);pending={id,resolve,reject,timer};
    worker!.postMessage({id,...snapshot,sampleRate:own.sampleRate});
   });
   return{promise,cancel(){if(pending?.id===id)closeWorker();}};
  },
 },report);
 return controller;
}
